import React from 'react'
import { Check, ArrowRight, X, Minus, Shield } from 'lucide-react'
import { featureFlags } from '../lib/featureFlags'

const PricingPage = () => {
  const allFeatures = [
    // Core Trust Center
    { text: 'Custom branded trust center with your logo & colors', flag: null },
    { text: 'Security badges & compliance attestations', flag: null },
    { text: 'Document library with gated access & AI-powered PDF text extraction', flag: null },
    { text: 'Custom domain support (e.g., trust.yourcompany.com)', flag: null },
    { text: 'Version control with preview/live/rollback capabilities', flag: null },
    { text: 'Interactive product tours & guided walkthroughs', flag: null },
    { text: 'Changelog management with automated customer notifications', flag: null },

    // NDA Management
    { text: '🏆 Best-in-class NDA workflow with 5 professional templates', flag: null },
    { text: 'NDA analytics dashboard with acceptance tracking & geographic insights', flag: null },
    { text: 'Clickwrap NDA with version control & expiration management', flag: null },
    { text: 'Email notifications for NDA confirmations & expiration reminders', flag: null },
    { text: 'CSV/PDF export for compliance audits with geolocation data', flag: null },

    // AI Knowledge Base
    { text: 'AI-powered knowledge base (CAIQ v4.0.3, 261 questions)', flag: null },
    { text: 'Natural language query system - ask questions, get instant answers', flag: null },
    { text: 'Bulk AI response generation (positive/negative scenarios)', flag: null },
    { text: 'Multi-select approval workflow for CAIQ answers', flag: null },
    { text: 'Automated document scanning & answer extraction', flag: null },
    { text: '🆕 Questionnaire library with 100+ pre-built security assessments', flag: 'questionnaire' },
    { text: '🆕 AI-powered questionnaire auto-fill from your knowledge base', flag: 'questionnaire' },

    // TPRM & Contract Management
    { text: '🆕 AI-powered vendor risk assessment with automated security scoring', flag: 'tprm' },
    { text: '🆕 Contract management with AI extraction (vendor, dates, values)', flag: 'tprm' },
    { text: '🆕 Automatic duplicate contract detection & management', flag: 'tprm' },
    { text: '🆕 Customer contract tracking with ARR calculations', flag: 'tprm' },
    { text: '🆕 Contract renewal alerts & expiration tracking', flag: 'tprm' },
    { text: '🆕 Vendor risk scoring with multi-dimensional analysis', flag: 'vendorRisk' },

    // Privacy & Compliance
    { text: '🆕 Privacy portal with GDPR/CCPA data request handling', flag: 'privacyPortal' },
    { text: '🆕 Automated data subject request workflows', flag: 'privacyPortal' },
    { text: 'Subprocessor management with automated review scheduling', flag: null },
    { text: 'Full audit trail for all changes & access', flag: null },
    { text: 'Compliance reporting & analytics', flag: null },
    { text: '🆕 AI Policy Generator for compliance documents', flag: 'policyGenerator' },

    // Integrations & Access
    { text: 'SSO/SAML integration (Okta, Azure AD)', flag: null },
    { text: 'Passkey authentication (WebAuthn) for passwordless login', flag: null },
    { text: 'CRM integrations (Salesforce, HubSpot, Pipedrive, Close)', flag: null },
    { text: 'Salesforce auto-approval for trusted customers', flag: null },
    { text: 'Slack notifications for access requests & approvals', flag: null },
    { text: 'Full REST API access with authentication', flag: null },

    // Analytics & Insights
    { text: '🆕 Revenue attribution tracking - link security to deals', flag: null },
    { text: '🆕 Engagement analytics & visitor insights', flag: null },
    { text: 'Real-time analytics & reporting dashboards', flag: null },
    { text: 'Access request tracking & management', flag: null },
    { text: '🆕 74-tool AI Agent assistant for trust center management', flag: 'aiAgent' },

    // Team Collaboration
    { text: 'Role-based access control (Admin/Security/Legal/Viewer)', flag: null },
    { text: 'Multi-user collaboration with approval workflows', flag: null },
    { text: 'Email notifications for access requests & updates', flag: null },
    { text: 'Team activity feed & audit logs', flag: null },

    // AI & Machine Trust
    { text: '🤖 Machine-readable REST APIs for automated compliance verification', flag: null },
    { text: '🔐 Zero-knowledge proofs of skill integrity (PLONK — no trusted setup)', flag: null },
    { text: '🔍 AI agent security scanning — OWASP LLM Top 10 coverage', flag: null },
    { text: '⚠️ Lethal trifecta detection (private data + untrusted input + exfiltration)', flag: null },
    { text: '📡 OpenTelemetry-compatible AI security telemetry export', flag: null },
    { text: '🏅 Cryptographic rank & skill attestation badges', flag: null },

    // Custom Content
    { text: '📄 Custom contracts (enterprise customers can use custom paper/terms)', flag: null },
    { text: '📄 Access to all Netlify security documentation', flag: null },
    { text: '24/7 community support', flag: null }
  ]

  // Filter features based on enabled flags
  const features = allFeatures.filter(feature =>
    !feature.flag || featureFlags[feature.flag as keyof typeof featureFlags]
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Header */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-blue-400" />
            <a href="/" className="ml-3 text-2xl font-bold text-white">TrustCenterPage.com</a>
          </div>
          <div className="flex items-center gap-6">
            <a href="/directory" className="text-gray-300 hover:text-white">Directory</a>
            <a href="/pricing" className="text-gray-300 hover:text-white">Pricing</a>
            <a href="/admin" className="text-gray-300 hover:text-white">Company Login</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Simple, Transparent Pricing
        </h1>
        <p className="text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
          $10 per active domain per month. All features included.
          No feature gates, no hidden fees.
        </p>
        <p className="text-md text-gray-400 mb-2">
          Start free with up to 25 active domains. After 25 domains, pay for all domains, averaged over 12 months.
        </p>
        <p className="text-sm text-blue-400 mt-2">
          Includes human compliance tools <em>and</em> AI agent security — one plan, everything.
        </p>
        <div className="mt-6 max-w-3xl mx-auto">
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-300 font-semibold mb-2">Included at no extra cost:</p>
            <p className="text-gray-300 text-sm">
              📄 Support for custom contracts (bring your own paper/terms)<br/>
              📄 Full access to all Netlify security documentation
              {(featureFlags.tprm || featureFlags.privacyPortal || featureFlags.questionnaire || featureFlags.aiAgent || featureFlags.policyGenerator || featureFlags.vendorRisk) && (
                <>
                  <br/>
                  🆕 Advanced features: {[
                    featureFlags.tprm && 'TPRM',
                    featureFlags.privacyPortal && 'Privacy Portal',
                    featureFlags.questionnaire && 'Questionnaire Library',
                    featureFlags.aiAgent && 'AI Agent',
                    featureFlags.policyGenerator && 'Policy Generator',
                    featureFlags.vendorRisk && 'Vendor Risk'
                  ].filter(Boolean).join(', ')}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Card */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="relative bg-gray-800/50 backdrop-blur-sm border border-blue-500 rounded-xl p-8 shadow-lg shadow-blue-500/20">
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center mb-3">
                <span className="text-5xl font-bold text-white">$10</span>
                <span className="text-gray-400 ml-2 text-xl">/active domain/month</span>
              </div>
              <p className="text-lg text-blue-400 font-semibold mb-2">
                First 25 active domains free
              </p>
              <p className="text-sm text-gray-400 italic">
                Averaged over prior 12 months
              </p>
              {featureFlags.invoiceBilling && (
              <p className="text-sm text-gray-400 mt-2">
                Invoice or credit card accepted
              </p>
              )}
            </div>

            <div className="border-t border-gray-700 pt-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4 text-center">
                Pricing Tiers
              </h3>
              <div className="bg-gray-900/50 rounded-lg p-4 mb-6 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">0-25 domains (12-month avg)</span>
                  <span className="text-green-400 font-semibold">FREE</span>
                </div>
                <div className="flex justify-between items-center border-t border-gray-700 pt-2">
                  <span className="text-gray-300">26-100 domains (12-month avg)</span>
                  <span className="text-blue-400 font-semibold">$10 × total domains</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm pl-4">Example: 50 domains</span>
                  <span className="text-gray-400 text-sm">$500/month</span>
                </div>
                <div className="flex justify-between items-center border-t border-gray-700 pt-2">
                  <span className="text-gray-300">100+ domains (12-month avg)</span>
                  <span className="text-blue-400 font-semibold">$10 × total domains</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm pl-4">Example: 100 domains</span>
                  <span className="text-gray-400 text-sm">$1,000/month</span>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-4 text-center">
                All Features Included
              </h3>
              <ul className="space-y-3 grid md:grid-cols-2 gap-x-6">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <Check className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => {
                window.location.href = '/admin'
              }}
              className="w-full py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition bg-blue-600 hover:bg-blue-700 text-white text-lg"
            >
              Get Started Free
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Feature Comparison Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="text-3xl font-bold text-white text-center mb-4">
          How We Compare
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          See how our trust center stacks up against the competition
        </p>

        <div className="overflow-x-auto">
          <table className="w-full bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Feature</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-blue-400 bg-blue-900/20">
                  <div>Trust Center</div>
                  <div className="text-xs font-normal text-gray-400 mt-1">(This Product)</div>
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">SafeBase</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Whistic</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Secureframe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {/* Pricing */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Starting Price</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <div className="text-green-400 font-semibold">Free</div>
                  <div className="text-xs text-gray-400">Up to 25 domains</div>
                </td>
                <td className="px-6 py-4 text-center text-gray-400">Custom</td>
                <td className="px-6 py-4 text-center text-gray-400">Custom</td>
                <td className="px-6 py-4 text-center text-gray-400">~$20k/yr</td>
              </tr>

              {/* Custom Branding */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Custom Branding</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* Document Library */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Document Library</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* Version Control */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Version Control & Rollback</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
              </tr>

              {/* Subprocessor Management */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Subprocessor Management</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* Analytics */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Visitor Analytics</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* SSO/SAML */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">SSO/SAML Integration</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* API Access */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">API Access</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* Custom Domain */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Custom Domain</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* AI Knowledge Base */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">AI-Powered Knowledge Base</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                  <div className="text-xs text-gray-300 mt-1 font-semibold">Best-in-Class</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                  <div className="text-xs text-gray-400 mt-1">Chatbot only</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                  <div className="text-xs text-gray-400 mt-1">Limited</div>
                </td>
              </tr>

              {/* CRM Integration */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">CRM Integrations</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                  <div className="text-xs text-gray-300 mt-1 font-semibold">Best-in-Class</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
              </tr>

              {/* Email Capture */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Email Capture & Forms</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                </td>
              </tr>

              {/* NDA Management */}
              <tr className="hover:bg-gray-700/30 bg-blue-900/5">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Advanced NDA Management</td>
                <td className="px-6 py-4 text-center bg-blue-900/20">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                  <div className="text-xs text-gray-300 mt-1 font-semibold">Best-in-Class</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                  <div className="text-xs text-gray-400 mt-1">Basic NDA only</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <Minus className="h-5 w-5 text-yellow-400 mx-auto" />
                  <div className="text-xs text-gray-400 mt-1">Limited</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
              </tr>

              {/* AI Agent Security */}
              <tr className="hover:bg-gray-700/30 bg-blue-900/5">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">AI Agent Security Scanning</td>
                <td className="px-6 py-4 text-center bg-blue-900/20">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                  <div className="text-xs text-gray-300 mt-1 font-semibold">OWASP LLM Top 10</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
              </tr>

              {/* ZK Proofs */}
              <tr className="hover:bg-gray-700/30 bg-blue-900/5">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Cryptographic Attestation (ZK Proofs)</td>
                <td className="px-6 py-4 text-center bg-blue-900/20">
                  <Check className="h-5 w-5 text-green-400 mx-auto" />
                  <div className="text-xs text-gray-300 mt-1 font-semibold">PLONK — no toxic waste</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
                <td className="px-6 py-4 text-center">
                  <X className="h-5 w-5 text-red-400 mx-auto" />
                </td>
              </tr>

              {/* Setup Time */}
              <tr className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-sm text-gray-300 font-medium">Setup Time</td>
                <td className="px-6 py-4 text-center bg-blue-900/10">
                  <div className="text-green-400 font-semibold">5 min</div>
                </td>
                <td className="px-6 py-4 text-center text-gray-400">2-4 weeks</td>
                <td className="px-6 py-4 text-center text-gray-400">1-2 weeks</td>
                <td className="px-6 py-4 text-center text-gray-400">1-3 weeks</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            * Competitor information based on publicly available pricing and feature documentation as of {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              What counts as an active domain?
            </h3>
            <p className="text-gray-300">
              An active domain is a unique external entity — either a human organization (identified by email domain)
              or an AI system acting on behalf of an organization — that accesses your trust center during a month.
              We count unique domains per month. Your internal team members don't count—
              only external parties (humans or automated systems) accessing your trust center.
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              How is the 12-month rolling average calculated?
            </h3>
            <p className="text-gray-300 mb-3">
              We count active domains each month, then average that across the previous 12 months. This smooths out
              seasonal fluctuations and gives you predictable pricing. Once you exceed 25 domains, you pay for ALL domains.
            </p>
            <p className="text-gray-300 mb-2 font-semibold">Example:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-2 text-sm">
              <li>Jan-Dec: 30, 35, 28, 40, 38, 32, 29, 31, 36, 34, 33, 30 active domains</li>
              <li className="font-semibold text-blue-300">12-month average: (30+35+28+40+38+32+29+31+36+34+33+30)/12 = 33 domains</li>
              <li className="font-semibold text-blue-300">Total billable: 33 domains × $10 = $330/month</li>
            </ul>
            <p className="text-gray-300 mt-3 text-sm italic">
              Note: Once your rolling average exceeds 25 domains, you're charged for all domains, not just those above 25.
            </p>
            <p className="text-gray-300 mt-3">
              For new customers, we average over available months (e.g., if you're in your 3rd month, we average over 3 months).
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              Why is pricing based on active domains?
            </h3>
            <p className="text-gray-300">
              We believe in value-based pricing. If your customers access your trust center and find it valuable,
              we get paid. If they don't, you don't pay. This aligns our success with yours—we both win when your
              customers engage with your trust center.
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              Do all plans include all features?
            </h3>
            <p className="text-gray-300">
              Yes! There's just one plan with all features included. There are no feature gates or limitations.
              Everyone gets the exact same features—the only difference is the number of active domains.
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              What happens when I exceed 25 domains?
            </h3>
            <p className="text-gray-300 mb-3">
              When your 12-month rolling average exceeds 25 active domains, you'll be billed $10/month for ALL domains.
              For example, if your rolling average is 100 domains, you'll pay $1,000/month (100 domains × $10).
              We'll notify you as you approach the threshold.
            </p>
            <p className="text-gray-300 text-sm font-semibold">
              Important: Once you exceed 25 domains, you pay for the total count, not just the overage.
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              Why a 12-month rolling average?
            </h3>
            <p className="text-gray-300">
              The 12-month rolling average prevents sudden price spikes from temporary usage increases
              (like a big product launch or seasonal traffic) and gives you predictable costs. If you have
              a seasonal business, you won't be penalized for monthly fluctuations.
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              What payment methods do you accept?
            </h3>
            <p className="text-gray-300">
              We accept {featureFlags.invoiceBilling ? 'both credit card and invoice payments' : 'credit card payments'}. You can set up your preferred payment method
              in your account dashboard once you exceed the free tier threshold.
            </p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              Does this work for AI agents and automated systems?
            </h3>
            <p className="text-gray-300 mb-3">
              Yes. Every trust center includes a full REST API, so AI agents can programmatically verify your security posture,
              pull compliance attestations, and check for policy changes — no human in the loop required.
            </p>
            <p className="text-gray-300 font-semibold mb-2">AI-specific capabilities included:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-2 text-sm">
              <li>Machine-readable API endpoints for all trust data</li>
              <li>Zero-knowledge proofs for cryptographic skill integrity verification</li>
              <li>AI agent security scanning (OWASP LLM Top 10)</li>
              <li>Lethal trifecta detection — flags concurrent private data access, untrusted input, and exfiltration capability</li>
              <li>OpenTelemetry-compatible telemetry export for your security stack</li>
            </ul>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              How does the AI-powered knowledge base work?
            </h3>
            <p className="text-gray-300 mb-3">
              Our AI scans your security documents and automatically generates answers to all 261 CAIQ v4.0.3
              (Consensus Assessments Initiative Questionnaire) questions. This saves you hours of manual work
              when responding to customer security questionnaires.
            </p>
            <p className="text-gray-300 font-semibold mb-2">You provide your own Anthropic API key:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-2 text-sm">
              <li>Keeps your costs transparent and under your control</li>
              <li>Estimated cost: ~$0.86-$2.80 per full CAIQ scan (261 questions)</li>
              <li>You only pay for scans you run, directly to Anthropic</li>
              <li>No platform markup or hidden AI fees from us</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 text-center">
        <div className="bg-blue-600 rounded-xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Start Free—Pay Only for What You Use
          </h2>
          <p className="text-xl text-blue-100 mb-4">
            Up to 25 active domains free, forever. All features included. No credit card required.
          </p>
          {featureFlags.invoiceBilling && (
          <p className="text-md text-blue-100 mb-8 opacity-90">
            Invoice or credit card processing available for paid domains.
          </p>
          )}
          {!featureFlags.invoiceBilling && (
          <p className="text-md text-blue-100 mb-8 opacity-90">
            Credit card processing available for paid domains.
          </p>
          )}
          <a
            href="/admin"
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition inline-flex items-center gap-2"
          >
            Get Started Free
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900/50 backdrop-blur-sm border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} Trust Center Platform. Built with Netlify.</p>
        </div>
      </footer>
    </div>
  )
}

export default PricingPage
